import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from './config/axios';

/**
 * Interface for location form data structure
 */
interface LocationFormData {
  name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  radius: number;
}

/**
 * Interface for location object structure (API response)
 */
interface Location {
  id: number;
  name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  radius: number;
  [key: string]: any; // Allow for additional properties that might come from the API
}

function LocationCreate(): React.JSX.Element {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<LocationFormData>({
    name: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    radius: 50,
  });

  const [error, setError] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'number' ? Number(value) : value 
    }));
  };

  const handleSave = async (): Promise<void> => {
    setError('');
    setIsSaving(true);

    try {
      await axiosInstance.post<Location>('/api/locations/', formData);
      navigate('/locations');
    } catch (err: any) {
      console.error('Error creating Location:', err);
      setError('Failed to create Location. Check console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (): void => {
    navigate('/locations');
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>Create New Location</h2>

      {error && (
        <div
          style={{
            marginBottom: '15px',
            color: 'red',
            border: '1px solid red',
            padding: '8px',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginBottom: '10px' }}>
        <label>Name:</label>
        <br />
        <input
          name="name"
          value={formData.name}
          onChange={handleChange}
          style={{ width: '300px' }}
          data-testid="location-name-input"
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Street:</label>
        <br />
        <input
          name="street"
          value={formData.street}
          onChange={handleChange}
          style={{ width: '300px' }}
          data-testid="location-street-input"
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>House Nr:</label>
        <br />
        <input
          name="house_number"
          value={formData.house_number}
          onChange={handleChange}
          style={{ width: '100px' }}
          data-testid="location-house-number-input"
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Postal Code:</label>
        <br />
        <input
          name="postal_code"
          value={formData.postal_code}
          onChange={handleChange}
          style={{ width: '120px' }}
          data-testid="location-postal-code-input"
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>City:</label>
        <br />
        <input
          name="city"
          value={formData.city}
          onChange={handleChange}
          style={{ width: '200px' }}
          data-testid="location-city-input"
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Radius (m):</label>
        <br />
        <input
          type="number"
          name="radius"
          value={formData.radius}
          onChange={handleChange}
          style={{ width: '100px' }}
          data-testid="location-radius-input"
        />
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving}
        style={{
          backgroundColor: '#002b80',
          color: '#fff',
          border: 'none',
          padding: '8px 12px',
          borderRadius: '4px',
          cursor: isSaving ? 'not-allowed' : 'pointer',
          marginRight: '8px',
        }}
        data-testid="save-location-button"
      >
        {isSaving ? 'Saving...' : 'Save'}
      </button>

      <button
        onClick={handleCancel}
        style={{
          backgroundColor: '#6c757d',
          color: '#fff',
          border: 'none',
          padding: '8px 12px',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
        data-testid="cancel-location-button"
      >
        Cancel
      </button>
    </div>
  );
}

export default LocationCreate; 